import { fetchVariantData as resolveVariant, isVariantFullyAvailable } from '@utilities/product';
import { toBundleParentVariantGid } from '@utilities/bundle-payload';

if (!customElements.get('product-bundle-item')) {
  /**
   * @classdesc A custom element that manages a single product within a bundle,
   * using option_value.id + fetch-based variant resolution.
   * @extends HTMLElement
   */
  class ProductBundleItem extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.init();
    }

    allowDebug() {
      return this.classList.contains('debug');
    }

    async init() {
      this.titleContainer = this.querySelector('.bundle-product__title');
      this.priceContainer = this.querySelector('.bundle-product__price');
      this.itemMessaging = this.querySelector('.bundle-product__messaging');
      this.optionGroups = this.querySelectorAll('.bundle-product__option');
      this.colorGroup = this.querySelector('.bundle-product__option.color-option');
      this.nonColorGroups = this.querySelectorAll('.bundle-product__option:not(.color-option)');
      this.selectedOptions = [];
      this.selectedValues = [];
      this.form = this.querySelector('.bundle-product__form');
      this.cartMessage = this.querySelector('.bundle-product__cart-error');
      this.submitButton = this.form?.querySelector('button[data-add-to-cart]');
      this.bundleContainer = this.closest('product-bundle-overview');
      this.variant = {};

      this.initEventListeners();

      const optionValueIds = this.getSelectedOptionValueIds();
      const data = await this.fetchVariantData(optionValueIds);

      if (data?.selected_variant) {
        this.variant = data.selected_variant;
        this.variant.order_type = this.dataset.orderType || 'WHP';
      }

      if (data?.options) {
        this.updateOptionsFromData(data.options);
      }

      this.updateState();
      this.dispatchEvent(new CustomEvent('product-bundle-item:initialized', {
        bubbles: true,
        detail: { variant: this.variant }
      }));
    }

    initEventListeners() {
      this.initOptionsListener();
      this.addEventListener('product-color-select:change', this.handleSwatchChange);
      this.form?.addEventListener('submit', this.submitToCart.bind(this));
    }

    updateATC() {
      if (!this.submitButton) return;

      if (this.cartMessage) {
        this.cartMessage.innerHTML = "";
        this.cartMessage.classList.remove('error');
        this.cartMessage.classList.add('hidden');
      }

      const buttonText = this.submitButton.querySelector('.button__text');
      const hasVariant = Boolean(this.variant?.id);

      this.submitButton.classList.toggle('no-variant', !hasVariant);

      if (isVariantFullyAvailable(this.variant)) {
        this.submitButton.classList.remove('disabled');
        this.submitButton.removeAttribute('disabled');
        this.submitButton.removeAttribute('aria-disabled');
        this.submitButton.dataset.state = 'add-to-bag';
        if (buttonText) {
          buttonText.innerHTML = this.submitButton.dataset.availableText;
        }
      } else {
        this.submitButton.classList.add('disabled');
        this.submitButton.setAttribute('disabled', 'disabled');
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.dataset.state = hasVariant ? 'sold-out' : 'sold-out';
        if (buttonText) {
          buttonText.innerHTML = this.submitButton.dataset.unavailableText;
        }
      }
    }

    /**
     * @param {Event} event
     */
    async submitToCart(event) {
      event.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled')) return;
      if (!isVariantFullyAvailable(this.variant)) return;

      try {
        this.clearCartMessage();
        this.setLoadingState(true);

        const properties = this.collectLineItemProperties();
        const item = {
          id: this.variant.id,
          quantity: 1,
          properties
        };

        const { addToCartViaStandardAction } = await import('@utilities/theme');
        await addToCartViaStandardAction(item, { context: 'product' });

        // Close a host quickview modal or slide-out drawer (shop-the-look
        // slider) after a successful individual add.
        const quickview = this.closest('quickview-modal');
        if (quickview) quickview.close();
        this.closest('slide-out-drawer')?.close();

        this.setLoadingState(false);
      } catch (error) {
        console.error('Bundle Item: Error adding to cart:', error);
        this.showCartError(error.message || 'Error adding to cart');
        this.setLoadingState(false);
      }
    }

    /**
     * Collect line item properties for bundle items
     * @returns {Object} Properties object
     */
    collectLineItemProperties() {
      const properties = {};

      properties["_ord_type"] = this.getOrderType();

      if (this.variant.hasOwnProperty("is_final_sale") && this.variant.is_final_sale === true) {
        properties["_Final Sale"] = true;
      }

      if (this.variant.hasOwnProperty("color")) {
        properties["_color_id"] = this.variant.color;
      }

      for (const el of this.form.querySelectorAll(`input[type="hidden"][name^="properties["]`)) {
        const propertyName = el.name.match(/\[([^)]+)\]/)?.[1];
        if (propertyName) properties[propertyName] = el.value;
      }

      // Partial-bundle tracking: this item was added on its own from a bundle
      // product's individual ATC (not the "add complete look" flow). Record which
      // bundle it belongs to so orders can report on partial-bundle line items.
      // Tracking-only by design — we intentionally omit `_bundled_id` and
      // `_bundled_type: "merge"`, which the cart (cart-json.liquid, cart-items.js)
      // uses to group/nest true merge-bundle pieces.
      const bundleContext = this.getBundleContext();
      if (bundleContext.title) {
        properties["_bundle_title"] = bundleContext.title;
        properties["_bundle_partial"] = true;
        if (bundleContext.parentVariantGid) {
          properties["_bundle_parent"] = bundleContext.parentVariantGid;
        }
      }

      properties["_add_to_cart_component"] = "product-bundle-item";

      return properties;
    }

    /**
     * Resolve the parent bundle's tracking context from the ancestor
     * `product-bundle-overview`. The overview owns the bundle title (`data-title`)
     * and the host parent variant id (via `getHostBundleParentVariantId()`),
     * keeping a single source of truth for both the complete-look and partial
     * add-to-cart paths.
     * @returns {{ title: string|null, parentVariantGid: string|null }}
     */
    getBundleContext() {
      const overview = this.bundleContainer;
      if (!overview) return { title: null, parentVariantGid: null };

      const title = overview.dataset?.title?.trim() || null;

      let parentVariantGid = null;
      if (typeof overview.getHostBundleParentVariantId === 'function') {
        parentVariantGid = toBundleParentVariantGid(overview.getHostBundleParentVariantId());
      }

      return { title, parentVariantGid };
    }

    /**
     * Set loading state for submit button
     * @param {boolean} isLoading - Whether to show loading state
     */
    setLoadingState(isLoading) {
      if (!this.submitButton) return;

      if (isLoading) {
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.submitButton.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');
      } else {
        this.submitButton.classList.remove('loading');
        this.submitButton.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
        this.updateATC();
      }
    }

    /**
     * Clear cart error message
     */
    clearCartMessage() {
      if (this.cartMessage) {
        this.cartMessage.innerHTML = "";
        this.cartMessage.classList.remove('error');
        this.cartMessage.classList.add('hidden');
      }
    }

    /**
     * Show cart error message
     * @param {string} message - Error message to display
     */
    showCartError(message) {
      if (this.cartMessage) {
        this.cartMessage.innerHTML = message;
        this.cartMessage.classList.add('error');
        this.cartMessage.classList.remove('hidden');
      }
    }

    /**
     * Handle swatch/color change from product-color-select child component
     * @param {CustomEvent} event
     */
    handleSwatchChange(event) {
      this.allowDebug() && console.log("handleSwatchChange", event);
      if (this.contains(event.target)) {
        this.allowDebug() && console.log('ProductBundleItem color change detected in a child product-color-select', event.target);
        this.allowDebug() && console.log('ProductBundleItem color', event.detail.swatch.value);
        this.handleOptionChange();
      }
    }

    initOptionsListener() {
      this.optionGroups = this.querySelectorAll('.bundle-product__option');
      this.nonColorGroups = this.querySelectorAll('.bundle-product__option:not(.color-option)');

      for (const optionGroup of this.nonColorGroups) {
        const optionInputs = optionGroup.querySelectorAll('input[type="radio"]');
        for (const optionInput of optionInputs) {
          optionInput.addEventListener("change", () => {
            this.handleOptionChange();
          });
        }
      }
    }

    /**
     * Get sorted array of selected option value IDs from all option groups.
     * @returns {string[]} Array of option value ID strings
     */
    getSelectedOptionValueIds() {
      const groups = Array.from(this.optionGroups).sort((a, b) => {
        const aKey = parseInt(a.dataset.optionKey.replace('option', ''), 10);
        const bKey = parseInt(b.dataset.optionKey.replace('option', ''), 10);
        return aKey - bKey;
      });

      const selectedIds = [];
      for (const group of groups) {
        const selectedInput = group.querySelector('input[type="radio"]:checked');
        const optionValueId = selectedInput?.dataset?.optionValueId;
        if (optionValueId) selectedIds.push(optionValueId);
      }

      return selectedIds;
    }

    /**
     * Fetch variant data using the shared VariantResolver utility.
     * @param {string[]} [optionValueIds=[]] - Selected option value IDs
     * @returns {Promise<Object|null>} Parsed JSON response or null on failure
     */
    async fetchVariantData(optionValueIds = []) {
      return resolveVariant(this.dataset.productUrl, optionValueIds);
    }

    /**
     * Handle option change — fetch new variant data and update state.
     */
    async handleOptionChange() {
      this.allowDebug() && console.log('product-bundle-item.js handleOptionChange()');

      const optionValueIds = this.getSelectedOptionValueIds();
      const data = await this.fetchVariantData(optionValueIds);

      if (data?.selected_variant) {
        this.variant = data.selected_variant;
        this.variant.order_type = this.dataset.orderType || 'WHP';
      } else {
        this.variant = {};
      }

      if (data?.options) {
        this.updateOptionsFromData(data.options);
      }

      this.updateState();

      this.dispatchEvent(new CustomEvent('product-bundle-item:variant-change', {
        bubbles: true,
        detail: { variant: this.variant }
      }));
    }

    /**
     * Update option availability using the options array from the fetch response.
     * Replaces the legacy updateOptionAvailability() that looped local variant data.
     * @param {Array} optionsData - The `options` array from the product-variant-json response
     */
    updateOptionsFromData(optionsData) {
      if (!optionsData || !Array.isArray(optionsData)) return;

      for (const option of optionsData) {
        if (!option.values || !Array.isArray(option.values)) continue;

        for (const value of option.values) {
          const optionValueId = String(value.id);
          const matchingInputs = this.querySelectorAll(
            `.product__option-input[data-option-value-id="${optionValueId}"]`
          );

          for (const input of matchingInputs) {
            const isImpossible = value.variant === null;
            const isUnavailable = value.available === false;

            input.classList.toggle('impossible-combination', isImpossible);
            input.classList.toggle('option-unavailable', isUnavailable && !isImpossible);

            if (isImpossible) {
              input.setAttribute('tabindex', '-1');
              input.setAttribute('disabled', '');
            } else {
              input.removeAttribute('tabindex');
              input.removeAttribute('disabled');
            }
          }
        }
      }
    }

    updateState() {
      this.allowDebug() && console.log('product-bundle-item.js updateState()');
      this.updateATC();

      if (Object.keys(this.variant).length > 0) {
        this.updateMediaElement();
        this.updateProductUrls();
        this.updateProductPrice();

        if (isVariantFullyAvailable(this.variant)) {
          this.itemMessaging.innerHTML = "";
          this.itemMessaging.classList.add('hidden');
        } else {
          this.itemMessaging.innerHTML = "Sorry, this size is unavailable";
          this.itemMessaging.classList.remove('hidden');
        }
      } else {
        this.itemMessaging.innerHTML = "Sorry, this size is unavailable";
        this.itemMessaging.classList.remove('hidden');

        if (this.priceContainer) {
          this.priceContainer.innerHTML = "";
        }
      }
    }

    updateMediaElement() {
      const mediaContainer = this.querySelector('.bundle-product__media');
      const mediaWrapper = mediaContainer?.querySelector('a');
      if (!mediaWrapper) return;

      // The variant endpoint serves card-sized (180px) media. A host that
      // displays larger (e.g. the shop-the-look quickshop) sets
      // data-media-width, and we rescale the CDN URL on the existing <img>
      // instead of adopting the endpoint's 180px markup.
      const targetWidth = parseInt(this.dataset.mediaWidth, 10) || 0;

      const newMediaHtml = this.variant?.featured_media_html;
      if (newMediaHtml && !targetWidth) {
        if (mediaWrapper.innerHTML !== newMediaHtml) {
          mediaWrapper.innerHTML = newMediaHtml;
        }
        return;
      }

      const featuredMediaUrl = this.variant?.featured_media_url;
      if (featuredMediaUrl) {
        const img = mediaWrapper.querySelector('img');
        if (img) {
          const width = targetWidth || 180;
          const url = targetWidth
            ? featuredMediaUrl.replace(/([?&]width=)\d+/, `$1${targetWidth}`)
            : featuredMediaUrl;
          img.setAttribute('src', url);
          // Keep srcset in sync so the browser does not continue using stale candidates.
          img.setAttribute('srcset', `${url} ${width}w`);
          img.removeAttribute('data-src');
          img.removeAttribute('data-srcset');
        }
      }
    }

    updateProductUrls() {
      const productURLs = this.querySelectorAll('.bundle-product__url');
      const newURL = this.variant?.url || '#';
      for (const productURL of productURLs) {
        productURL.href = newURL;
      }
    }

    updateProductPrice() {
      if (!this.priceContainer) return;
      if (!this.variant?.price_html) return;

      let html = this.variant.price_html;
      if (this.variant.is_final_sale) {
        const label = window.theme?.language_strings?.variant_final_sale || 'Final Sale';
        html += ` <span class="color-red">${label}</span>`;
      }
      this.priceContainer.innerHTML = html;
    }

    /**
     * @returns {Object} The current selected variant
     */
    getVariant() {
      return this.variant;
    }

    /**
     * Minimal product data sourced from data attributes.
     * Preserves the public API consumed by swym-custom-add-to-wishlist.js.
     * @returns {Object} Product object with id and title
     */
    getProduct() {
      return {
        id: parseInt(this.dataset.productId, 10) || null,
        title: this.dataset.productTitle || this.titleContainer?.textContent?.trim() || '',
        vendor: this.dataset.productVendor || '',
        type: this.dataset.productType || '',
      };
    }

    /**
     * @returns {string} The order type for this bundle item
     */
    getOrderType() {
      return this.dataset.orderType || 'WHP';
    }
  }

  customElements.define('product-bundle-item', ProductBundleItem);
}
